package top.wcpe.allincore.ui.runtime.ir.prop

import top.wcpe.allincore.ui.runtime.element.ItemStack
import top.wcpe.allincore.ui.runtime.element.RadioButton
import top.wcpe.allincore.ui.runtime.element.Slot
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.api.ItemView
import top.wcpe.allincore.ui.runtime.ir.eval.ValueCoercion
import top.wcpe.allincore.ui.runtime.render.StaticItemNbt

/**
 * Slot 元素属性贡献者：注册数据绑定 / 交互控制 / 预测开关 / 状态视觉 / 纹理背景 / 基础控制。
 *
 * 能力概览：
 * - 状态视觉：hover/pressed/focus/disabled/warn 每状态独立属性
 * - 纹理背景：按状态切换贴图
 * - 基础控制：showBackground / showTooltip / showCount / backgroundColor
 * - 子组件布局：Slot 支持 column 内嵌子组件
 */
internal object SlotPropContributor {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.prop.SlotPropContributor")

    @Suppress("LongMethod", "CyclomaticComplexMethod")
    fun contribute(m: MutableMap<String, PropBinding>) {
        // ---------- 数据绑定 ----------
        // DSL source 属性（如 "player.hotbar"）映射到 Slot.groupId，运行时由 SlotRenderer 查 ClientInventoryStore 取数据。
        m.slot("source") { s, v -> v?.let { s.groupId(it.toString()) } }
        // 显式声明的 key 覆盖基于 index 推导出的 "slot_N" 默认值。
        m.slot("key") { s, v -> v?.let { s.key(it.toString()) } }
        m.slot("index") { s, v ->
            if (v == null) return@slot
            // 仅在用户未显式设置 key 时，回退到默认的 "slot_<index>" 命名。
            val key = s.key()
            if (key.isNullOrEmpty() || key.startsWith("slot_")) {
                s.key("slot_" + ValueCoercion.toFloat(v).toInt())
            }
        }
        // wiki 字面量 slotIndex 与 index 同义。
        m.slot("slotIndex") { s, v ->
            if (v == null) return@slot
            val key = s.key()
            if (key.isNullOrEmpty() || key.startsWith("slot_")) {
                s.key("slot_" + ValueCoercion.toFloat(v).toInt())
            }
        }
        m.slot("slotSize") { s, v -> v?.let { s.slotSize(ValueCoercion.toFloat(it)) } }
        // 动态 slot.read key：求值后的字符串存到 Slot，由 IrInterpreter.buildElement 在 props 应用完成后注册信号。
        m.slot("readId") { s, v -> s.readId(v?.toString()) }

        // ---------- 静态物品（ItemStack） ----------
        // 复用 ItemStack 所有属性（showBackground / enchanted / tooltip 等），tooltip 自动生成。
        m["item"] = PropBinding.writeOnly { el, v ->
            if (el !is Slot) return@writeOnly
            // ItemView 直取镜像槽活物品 NBT；Map 从载荷 nbtSnbt 还原 NBT。两条路径都能带 customNbt 命中自定义 bbmodel / item-texture。
            when (v) {
                null -> el.staticItem(null)
                is ItemView -> el.staticItem(createItemStackFromItemView(v))
                is Map<*, *> -> el.staticItem(createItemStackFromMap(v))
            }
        }

        // ---------- 几何覆盖 ----------
        m.slot("itemSize") { s, v -> s.itemSize(floatOrNull(v)) }
        m.slot("itemInsetX") { s, v -> s.itemInsetX(floatOrNull(v)) }
        m.slot("itemInsetY") { s, v -> s.itemInsetY(floatOrNull(v)) }
        m.slot("quickCraftPreviewAlpha") { s, v -> s.quickCraftPreviewAlpha(floatOrNull(v)) }

        // ---------- 边框圆角（borderColor/borderWidth 同时支持 Slot 专属字段与其它元素的 StyleVisual） ----------
        m["borderColor"] = PropBinding.of(
            { el, v ->
                if (v != null) {
                    if (el is Slot) {
                        el.borderColor(ValueCoercion.toIntColor(v))
                    } else {
                        el.style().visual().borderColor(ValueCoercion.toIntColor(v))
                    }
                }
            },
            { el -> if (el is Slot) el.borderColor() else el.style().visual().borderColor() },
        )
        m["borderWidth"] = PropBinding.of(
            { el, v ->
                if (v != null) {
                    if (el is Slot) {
                        el.borderWidth(ValueCoercion.toFloat(v))
                    } else {
                        el.style().visual().borderWidth(ValueCoercion.toFloat(v))
                    }
                }
            },
            { el ->
                if (el is Slot) {
                    el.borderWidth()?.toDouble()
                } else {
                    el.style().visual().borderWidth().toDouble()
                }
            },
        )

        // ---------- 选中状态 ----------
        m["selected"] = PropBinding.writeOnly { el, v ->
            when (el) {
                is Slot -> el.selected(v?.let { ValueCoercion.toBool(it) })
                is RadioButton -> if (v != null) el.selected(ValueCoercion.toBool(v))
                else -> PropDiag.unsupported(LOGGER, el, "selected")
            }
        }
        m.slot("selectedOutlineColor") { s, v -> s.selectedOutlineColor(colorOrNull(v)) }
        m.slot("selectedOutlineWidth") { s, v -> s.selectedOutlineWidth(floatOrNull(v)) }
        m.slot("selectedOverlayColor") { s, v -> s.selectedOverlayColor(colorOrNull(v)) }

        // ---------- 状态视觉覆盖 ----------
        m.slot("hoverOverlayColor") { s, v -> s.hoverOverlayColor(colorOrNull(v)) }
        m.slot("hoverOverlayOpacity") { s, v -> s.hoverOverlayOpacity(floatOrNull(v)) }
        m.slot("hoverTexture") { s, v -> s.hoverTexture(v?.toString()) }
        m.slot("pressedOverlayColor") { s, v -> s.pressedOverlayColor(colorOrNull(v)) }
        m.slot("pressedOverlayOpacity") { s, v -> s.pressedOverlayOpacity(floatOrNull(v)) }
        m.slot("pressedTexture") { s, v -> s.pressedTexture(v?.toString()) }
        m.slot("focusOverlayColor") { s, v -> s.focusOverlayColor(colorOrNull(v)) }
        m.slot("focusBorderColor") { s, v -> s.focusBorderColor(colorOrNull(v)) }
        m.slot("focusTexture") { s, v -> s.focusTexture(v?.toString()) }
        m.slot("disabledOpacity") { s, v -> s.disabledOpacity(floatOrNull(v)) }
        m.slot("disabledOverlayColor") { s, v -> s.disabledOverlayColor(colorOrNull(v)) }
        m.slot("disabledTexture") { s, v -> s.disabledTexture(v?.toString()) }
        m.slot("warnOverlayColor") { s, v -> s.warnOverlayColor(colorOrNull(v)) }

        // ---------- 反馈动画色覆盖（null 回退主题对应 token） ----------
        m.slot("successColor") { s, v -> s.successColor(colorOrNull(v)) }
        m.slot("failedColor") { s, v -> s.failedColor(colorOrNull(v)) }
        m.slot("timeoutColor") { s, v -> s.timeoutColor(colorOrNull(v)) }

        // ---------- 背景内缩边框宽度覆盖（null 回退默认 1px） ----------
        m.slot("backgroundBorderWidth") { s, v -> s.backgroundBorderWidth(floatOrNull(v)) }

        // ---------- 纹理背景 ----------
        m.slot("backgroundTexture") { s, v -> s.backgroundTexture(v?.toString()) }
        m.slot("hoverBackgroundTexture") { s, v -> s.hoverBackgroundTexture(v?.toString()) }
        m.slot("pressedBackgroundTexture") { s, v -> s.pressedBackgroundTexture(v?.toString()) }
        m.slot("focusBackgroundTexture") { s, v -> s.focusBackgroundTexture(v?.toString()) }
        m.slot("disabledBackgroundTexture") { s, v -> s.disabledBackgroundTexture(v?.toString()) }

        // ---------- 基础控制 ----------
        // showBackground 同时支持 Slot 和 ItemStack：合并到一个 binding，避免与 StructurePropContributor 同名 key 互相覆盖。
        m["showBackground"] = PropBinding.writeOnly { el, v ->
            if (v != null) {
                when (el) {
                    is Slot -> el.showBackground(ValueCoercion.toBool(v))
                    is ItemStack -> el.showBackground(ValueCoercion.toBool(v))
                    else -> PropDiag.unsupported(LOGGER, el, "showBackground")
                }
            }
        }
        m.slot("backgroundColor") { s, v -> s.backgroundColor(colorOrNull(v)) }
        m.slot("showTooltip") { s, v -> s.showTooltip(v?.let { ValueCoercion.toBool(it) }) }
        m.slot("showCount") { s, v -> s.showCount(v?.let { ValueCoercion.toBool(it) }) }

        // 数量徽标锚点与偏移：同时支持 Slot 和 ItemStack（仿 showBackground 双类型分支）。
        m["countAnchor"] = PropBinding.writeOnly { el, v ->
            val anchor = v?.toString()
            when (el) {
                is Slot -> el.countAnchor(anchor)
                is ItemStack -> el.countAnchor(anchor)
                else -> PropDiag.unsupported(LOGGER, el, "countAnchor")
            }
        }
        m["countOffsetX"] = PropBinding.writeOnly { el, v ->
            val offset = floatOrNull(v)
            when (el) {
                is Slot -> el.countOffsetX(offset)
                is ItemStack -> el.countOffsetX(offset)
                else -> PropDiag.unsupported(LOGGER, el, "countOffsetX")
            }
        }
        m["countOffsetY"] = PropBinding.writeOnly { el, v ->
            val offset = floatOrNull(v)
            when (el) {
                is Slot -> el.countOffsetY(offset)
                is ItemStack -> el.countOffsetY(offset)
                else -> PropDiag.unsupported(LOGGER, el, "countOffsetY")
            }
        }
        m.slot("placeholderInset") { s, v -> s.placeholderInset(floatOrNull(v)) }

        // ---------- 交互控制 ----------
        m.slot("readOnly") { s, v -> v?.let { s.readOnly(ValueCoercion.toBool(it)) } }
        m.slot("allowShiftTransfer") { s, v -> v?.let { s.allowShiftTransfer(ValueCoercion.toBool(it)) } }
        m.slot("allowDrop") { s, v -> v?.let { s.allowDrop(ValueCoercion.toBool(it)) } }
        m.slot("allowPick") { s, v -> v?.let { s.allowPick(ValueCoercion.toBool(it)) } }
        m.slot("slotType") { s, v -> v?.let { s.slotType(it.toString()) } }

        // ---------- 预测开关 ----------
        m.slot("predict") { s, v -> s.predict(v?.let { ValueCoercion.toBool(it) }) }
    }

    /**
     * 注册一个仅作用于 [Slot] 的 writeOnly 绑定：统一处理「元素非 Slot → 记录 unsupported 并跳过」，
     * [body] 只需关注 Slot 分支。
     */
    private fun MutableMap<String, PropBinding>.slot(key: String, body: (Slot, Any?) -> Unit) {
        this[key] = PropBinding.writeOnly { el, v ->
            if (el !is Slot) {
                PropDiag.unsupported(LOGGER, el, key)
                return@writeOnly
            }
            body(el, v)
        }
    }

    /** null 透传的颜色强转：值为 null 时返回 null，否则按颜色解析。 */
    private fun colorOrNull(v: Any?): Int? = v?.let { ValueCoercion.toIntColor(it) }

    /** null 透传的浮点强转：值为 null 时返回 null，否则按浮点解析。 */
    private fun floatOrNull(v: Any?): Float? = v?.let { ValueCoercion.toFloat(it) }

    /** 将 Map 转为 ItemStack，复用 SlotStaticItem.fromMap 的解析逻辑。 */
    @Suppress("MagicNumber")
    private fun createItemStackFromMap(map: Map<*, *>): ItemStack {
        val itemId = map["itemId"] as? String ?: ""
        val count = (map["count"] as? Number)?.toInt() ?: 1
        val durability = (map["durability"] as? Number)?.toFloat() ?: -1f
        val enchanted = map["enchanted"] as? Boolean ?: false
        val lore = (map["lore"] as? List<*>)?.map { it?.toString() ?: "" } ?: emptyList()
        val displayName = map["displayName"] as? String ?: ""
        val stack = ItemStack.create()
            .itemId(itemId)
            .count(count)
            .durability(durability)
            .enchanted(enchanted)
            .lore(lore)
            .displayName(displayName)
        // 数据驱动 slot 也带上物品完整 NBT：服务端可在 item map 里附 nbtSnbt(SNBT 字符串)，此处还原为
        // customNbt，使 texture-rules / model-rules 命中自定义 bbmodel/贴图（与 createItemStackFromItemView
        // 的 nbtAll() 等价）。无 nbtSnbt / 解析失败时保持无 NBT，行为与旧版一致。
        StaticItemNbt.mapOf(map["nbtSnbt"] as? String)?.let { stack.customNbt(it) }
        return stack
    }

    /**
     * 将 `slot.read(...)` 得到的 [ItemView] 转为静态展示用 [ItemStack]。
     *
     * 与 [createItemStackFromMap] 的区别：NBT 直接取自「镜像的真实槽位」活物品（[ItemView.nbtAll]），
     * 而非静态载荷里的 nbtSnbt——用于把某个真实槽位的物品整体转传给另一个只读展示槽位；两者产出的
     * customNbt 语义一致，故自定义 bbmodel / item-texture 规则在两条路径上都能命中。
     */
    private fun createItemStackFromItemView(view: ItemView): ItemStack {
        // ItemView.itemId()/nbtAll()/lore() 契约非空，故无需原 Java 的 ?:"" / != null 兜底。
        val stack = ItemStack.create()
            .itemId(view.itemId())
            .count(view.count())
            .enchanted(view.enchanted())
            .customNbt(view.nbtAll())
        view.durability()?.let { stack.durability(it) }
        val displayName = view.displayName()
        if (!displayName.isNullOrEmpty()) stack.displayName(displayName)
        val lore = view.lore()
        if (lore.isNotEmpty()) stack.lore(lore)
        return stack
    }
}
